// src/hooks/useStreamingText.ts

import { useState, useEffect, useCallback } from 'react';
import { AnimationConfig } from '../config/animationConfig';

interface TextSegment {
  text: string;
  isTag: boolean;
  tagStart?: boolean;
  tagEnd?: boolean;
  isSpace?: boolean;
}

interface TextChunk {
  content: TextSegment[];
  html: string;
}

export const useStreamingText = (
  text: string,
  startDelay: number = AnimationConfig.text.startDelay,
  chunkDelay: number = AnimationConfig.text.chunkDelay
) => {
  const [chunks, setChunks] = useState<TextChunk[]>([]);
  const [visibleChunks, setVisibleChunks] = useState<number>(0);

  const parseText = useCallback((input: string): TextSegment[] => {
    const result: TextSegment[] = [];
    let buffer = '';
    let inTag = false;
    let lastWasTag = false;

    const flushBuffer = () => {
      if (buffer) {
        const words = buffer.split(/(\s+)/).filter(w => w.length > 0);
        words.forEach(word => {
          result.push({
            text: word,
            isTag: false,
            isSpace: /^\s+$/.test(word)
          });
        });
        buffer = '';
      }
    };

    for (let i = 0; i < input.length; i++) {
      const char = input[i];

      if (char === '<') {
        flushBuffer();
        inTag = true;
        buffer = char;
      } else if (char === '>' && inTag) {
        buffer += char;
        result.push({
          text: buffer,
          isTag: true,
          tagStart: !buffer.startsWith('</'),
          tagEnd: buffer.startsWith('</'),
        });
        buffer = '';
        inTag = false;
        lastWasTag = true;
      } else if (inTag) {
        buffer += char;
      } else {
        if (lastWasTag && char === ' ') {
          result.push({ text: ' ', isTag: false, isSpace: true });
          lastWasTag = false;
        } else {
          buffer += char;
          lastWasTag = false;
        }
      }
    }

    flushBuffer();
    return result;
  }, []);

  const createChunks = useCallback((segments: TextSegment[]): TextChunk[] => {
    const chunks: TextChunk[] = [];
    let currentChunk: TextSegment[] = [];
    let wordCount = 0;
    let openTags: string[] = [];

    const finalizeChunk = () => {
      if (currentChunk.length > 0) {
        const html = currentChunk
          .map(segment => segment.text)
          .join('');
        chunks.push({ content: [...currentChunk], html });
        currentChunk = [];
        wordCount = 0;
      }
    };

    segments.forEach((segment) => {
      if (segment.isTag) {
        if (segment.tagStart) {
          openTags.push(segment.text);
        } else if (segment.tagEnd) {
          openTags.pop();
        }
        currentChunk.push(segment);
      } else if (!segment.isSpace) {
        wordCount++;
        currentChunk.push(segment);

        if (wordCount >= AnimationConfig.text.wordsPerChunk ||
          segment.text.match(/[.!?,;]$/)) {
          finalizeChunk();
        }
      } else {
        currentChunk.push(segment);
      }
    });

    finalizeChunk();
    return chunks;
  }, []);

  useEffect(() => {
    setVisibleChunks(0);
    const parsedSegments = parseText(text);
    const textChunks = createChunks(parsedSegments);
    setChunks(textChunks);

    let timeoutId: NodeJS.Timeout;
    let intervalId: NodeJS.Timeout;

    const startAnimation = () => {
      intervalId = setInterval(() => {
        setVisibleChunks(prev => {
          if (prev >= textChunks.length) {
            clearInterval(intervalId);
            return prev;
          }
          return prev + 1;
        });
      }, chunkDelay);
    };

    timeoutId = setTimeout(startAnimation, startDelay);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [text, startDelay, chunkDelay, parseText, createChunks]);

  return { chunks, visibleChunks };
};